import type { MbcBrandContext } from "@nlc/shared-types";

import { Sparkles } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import { Label } from "@/components/field";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";
import { useGetBrandsByProjectId } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";

import { mapBrandContext } from "../lib/map-brand-context";

type BrandGateProps = {
  onCreateNewBrand: () => void;
  /** The full brand context, or null for "continue without a brand". */
  onResolved: (brand: MbcBrandContext | null) => void;
  projectId?: number;
};

/**
 * Deterministic opening step of the MBC conversation: the brand question.
 *
 * "Brand question initiates the conversation" — before the agent starts, the
 * customer picks an existing brand, creates a new one, or continues without a
 * brand. The full brand (fetched from the backend) is handed back to the page,
 * which starts the agent with it as request-context grounding (not working
 * memory). Fetching here means the agent's session is created once with the
 * brand already resolved.
 */
export function BrandGate({
  onCreateNewBrand,
  onResolved,
  projectId,
}: BrandGateProps) {
  const [step, setStep] = useState<"choose" | "pick-existing">("choose");
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const { canAccess } = useRoleAccess();
  const canCreateBrand = canAccess("create-brand-button");

  const { data: brands = [], isLoading } = useGetBrandsByProjectId(projectId);
  const selectableBrands = brands.filter((brand) => brand.id !== 0);

  const { data: selectedBrand, isFetching: isFetchingBrand } = useGetBrandById(
    Number(selectedBrandId) || 0,
  );

  const handleConfirm = () => {
    if (!selectedBrand) return;
    onResolved(mapBrandContext(selectedBrand));
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col rounded-2xl border border-border-secondary bg-bg-secondary">
      <div className="flex flex-1 flex-col gap-lg overflow-y-auto px-3xl py-xl">
        <div className="flex w-full justify-start gap-md">
          <div className="mt-xs flex size-8 shrink-0 items-center justify-center rounded-full bg-utility-brand-100 text-fg-brand-primary">
            <Sparkles className="size-4" />
          </div>

          <div className="max-w-[85%] rounded-2xl border border-border-secondary bg-bg-primary px-lg py-md text-text-primary">
            <p className="text-sm">
              Hi 👋 Let&apos;s build your brief together.
            </p>
            <p className="mt-xs text-sm">
              Do you want to work with an{" "}
              <span className="font-semibold">existing brand</span>
              {canCreateBrand ? " or create a new one?" : "?"}
            </p>

            {step === "choose" ? (
              <div className="mt-md flex flex-wrap gap-sm">
                <Button
                  className="rounded-full"
                  onPress={() => setStep("pick-existing")}
                  size="sm"
                  variant="secondary"
                >
                  Use an existing brand
                </Button>
                {canCreateBrand && (
                  <Button
                    className="rounded-full"
                    onPress={onCreateNewBrand}
                    size="sm"
                    variant="secondary"
                  >
                    Create a new brand
                  </Button>
                )}
                <Button
                  className="rounded-full"
                  onPress={() => onResolved(null)}
                  size="sm"
                  variant="secondary"
                >
                  Continue without a brand
                </Button>
              </div>
            ) : (
              <div className="mt-md flex flex-col gap-sm rounded-xl border border-border-secondary bg-bg-primary p-md">
                <Label>What brand are we creating the request for?</Label>
                <select
                  className="rounded-lg border border-border-primary bg-bg-primary px-md py-sm text-sm text-text-primary outline-none"
                  disabled={isLoading}
                  onChange={(event) => setSelectedBrandId(event.target.value)}
                  value={selectedBrandId}
                >
                  <option value="">
                    {isLoading ? "Loading brands..." : "Choose a brand..."}
                  </option>
                  {selectableBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-sm">
                  <Button
                    className="self-start"
                    isDisabled={!selectedBrandId || isFetchingBrand}
                    onPress={handleConfirm}
                    size="sm"
                    variant="primary"
                  >
                    {isFetchingBrand ? "Loading brand..." : "Confirm brand"}
                  </Button>
                  <Button
                    className="self-start"
                    onPress={() => {
                      setSelectedBrandId("");
                      setStep("choose");
                    }}
                    size="sm"
                    variant="secondary"
                  >
                    Back
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
